/**
 * SecureStorage - Origin-bound encrypted localStorage
 * 
 * This service provides military-grade encryption for localStorage data.
 * Data is encrypted using keys derived from:
 * 1. User's session key (from server)
 * 2. Browser origin (domain)
 * 3. A unique device fingerprint
 * 
 * Even if someone copies localStorage data, they CANNOT decrypt it because:
 * - The encryption key is never stored, it's derived at runtime
 * - The key derivation requires the exact same origin
 * - The key is non-exportable (using Web Crypto API)
 * - Additional entropy from device fingerprint
 */

class SecureStorage {
  constructor() {
    this.cryptoKey = null;
    this.origin = window.location.origin;
    this.initialized = false;
    this.storagePrefix = 'rfid_secure_';
    this.keyDerivationIterations = 310000; // OWASP recommended for PBKDF2-SHA256
    this._db = null; // IndexedDB reference
  }

  _makeStorageKey(key) {
    return `${this.storagePrefix}${this.userId}_${key}`;
  }

  async _idbGet(storageKey) {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('kv', 'readonly');
      const req = tx.objectStore('kv').get(storageKey);
      req.onsuccess = () => resolve(req.result);
      req.onerror = (e) => reject(e);
    });
  }

  async _idbPut(storageKey, value) {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('kv', 'readwrite');
      tx.objectStore('kv').put(value, storageKey);
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e);
    });
  }

  async _idbDelete(storageKey) {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('kv', 'readwrite');
      tx.objectStore('kv').delete(storageKey);
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e);
    });
  }

  async _getEncrypted(storageKey) {
    // Try IndexedDB first
    try {
      const encryptedData = await this._idbGet(storageKey);
      if (encryptedData) return encryptedData;
    } catch {
      // ignore
    }
    // Fallback to localStorage
    try {
      return localStorage.getItem(storageKey);
    } catch {
      return null;
    }
  }

  async _setEncrypted(storageKey, encryptedData, { preferIDB = false } = {}) {
    if (preferIDB) {
      try {
        await this._idbPut(storageKey, encryptedData);
        try { localStorage.removeItem(storageKey); } catch {}
        return;
      } catch (e) {
        console.warn('[SecureStorage] IndexedDB write failed, trying localStorage:', e);
      }
    }

    try {
      localStorage.setItem(storageKey, encryptedData);
    } catch {
      // last resort: try IDB if localStorage fails
      try {
        await this._idbPut(storageKey, encryptedData);
      } catch (e) {
        console.error('[SecureStorage] Both IndexedDB and localStorage failed for storageKey:', storageKey, e);
      }
    }
  }

  _removeEncrypted(storageKey) {
    try { localStorage.removeItem(storageKey); } catch {}
    // Also remove from IndexedDB (async, fire-and-forget)
    this.openDB().then(db => {
      const tx = db.transaction('kv', 'readwrite');
      tx.objectStore('kv').delete(storageKey);
    }).catch(() => {});
  }

  async _getItemSimple(key) {
    const storageKey = this._makeStorageKey(key);

    // Try IndexedDB first
    try {
      const encryptedData = await this._idbGet(storageKey);
      if (encryptedData) {
        try { return await this.decrypt(encryptedData); }
        catch (e) {
          // Key is derived from the session token: after a re-login the old
          // cache is unreadable by design. Drop it and let callers refetch.
          console.warn('SecureStorage: stale cache entry dropped:', key);
          try { await this._idbDelete(storageKey); } catch { /* ignore */ }
          return null;
        }
      }
    } catch {
      // IndexedDB not available, continue to localStorage
    }

    const encryptedData = (() => {
      try { return localStorage.getItem(storageKey); } catch { return null; }
    })();
    if (!encryptedData) return null;

    try {
      return await this.decrypt(encryptedData);
    } catch (error) {
      console.warn('SecureStorage: stale cache entry dropped:', key);
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
      return null;
    }
  }

  async _setItemSimple(key, data) {
    const encryptedData = await this.encrypt(data);
    const storageKey = this._makeStorageKey(key);

    const shouldUseIDB = encryptedData.length > 500000;
    await this._setEncrypted(storageKey, encryptedData, { preferIDB: shouldUseIDB });
  }



  /**
   * Generate a unique device fingerprint
   * This adds another layer of protection - data encrypted on one device
   * cannot be decrypted on another
   */
  async generateDeviceFingerprint() {
    const components = [
      navigator.userAgent,
      navigator.language,
      new Date().getTimezoneOffset(),
      window.screen.width + 'x' + window.screen.height,
      window.screen.colorDepth,
      navigator.hardwareConcurrency || 'unknown',
      navigator.deviceMemory || 'unknown',
      // Canvas fingerprint
      await this.getCanvasFingerprint(),
      // WebGL fingerprint
      this.getWebGLFingerprint()
    ];

    const fingerprintString = components.join('|');
    const encoder = new TextEncoder();
    const data = encoder.encode(fingerprintString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Canvas fingerprint for device identification
   */
  async getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 200;
      canvas.height = 50;
      
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('RFID Vault Security', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Origin Bound Key', 4, 17);
      
      return canvas.toDataURL();
    } catch (e) {
      return 'canvas-not-supported';
    }
  }

  /**
   * WebGL fingerprint for additional device identification
   */
  getWebGLFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return 'webgl-not-supported';
      
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        return `${vendor}|${renderer}`;
      }
      return 'webgl-no-debug';
    } catch (e) {
      return 'webgl-error';
    }
  }

  /**
   * Initialize the secure storage with session credentials
   * @param {string} sessionKey - The user's session/auth token (used for validation, not encryption)
   * @param {string} userId - The user's ID (rfidId)
   */
  async initialize(sessionKey, userId) {
    if (!sessionKey || !userId) {
      throw new Error('Session key and user ID are required for secure storage');
    }

    const deviceFingerprint = await this.generateDeviceFingerprint();
    
    // IMPORTANT: We do NOT include sessionKey in the key derivation!
    // The sessionKey changes on each login, which would make cached data unreadable.
    // Instead, we derive the key from:
    // - origin: ensures data encrypted on one domain can't be read on another
    // - userId: ensures different users have different encryption keys
    // - deviceFingerprint: ensures data can only be decrypted on the same device/browser
    //
    // Security is maintained because:
    // 1. The cryptoKey is never stored, only derived at runtime
    // 2. Without the exact same browser/device, decryption is impossible
    // 3. Without the exact same origin, decryption is impossible
    // 4. The session token is still required to LOGIN (authentication)
    // 5. The encrypted cache is useless without being able to authenticate
    const keyMaterial = `${this.origin}::${userId}::${deviceFingerprint}::rfid_vault_persistent_key_v2`;
    
    // Convert to bytes for key derivation
    const encoder = new TextEncoder();
    const keyMaterialBytes = encoder.encode(keyMaterial);
    
    // Import the key material for PBKDF2
    const baseKey = await crypto.subtle.importKey(
      'raw',
      keyMaterialBytes,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    // Create a salt based on origin and user (consistent across sessions)
    const saltString = `rfid_vault_salt_v2_${this.origin}_${userId}`;
    const salt = encoder.encode(saltString);

    // Derive the actual encryption key using PBKDF2
    // The key is marked as non-extractable for extra security
    this.cryptoKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: this.keyDerivationIterations,
        hash: 'SHA-256'
      },
      baseKey,
      {
        name: 'AES-GCM',
        length: 256
      },
      false, // NOT extractable - key cannot be exported
      ['encrypt', 'decrypt']
    );

    this.initialized = true;
    this.userId = userId;

    this._cleanupStaleStorage().catch(() => {});

    console.log('SecureStorage initialized with origin-bound persistent encryption');
    return true;
  }

  async _cleanupStaleStorage() {
    if (!this.userId) return;
    const currentDbName = `rfid_vault_${this.userId}`;
    const currentLsPrefix = `${this.storagePrefix}${this.userId}_`;

    // Drop ALL rfid_vault_* IDB databases except the current user's.
    if (typeof indexedDB?.databases === 'function') {
      try {
        const dbs = await indexedDB.databases();
        for (const db of dbs) {
          if (db?.name?.startsWith('rfid_vault_') && db.name !== currentDbName) {
            try { indexedDB.deleteDatabase(db.name); } catch {}
          }
        }
      } catch {}
    }

    // Drop legacy credentials cache keys from current user (in case user upgraded
    // from a build that wrote chunked vault data). The new code never writes them.
    const LEGACY_PREFIXES = [
      'credentials_cache',
      'credentials_last_global_update',
      'credentials_cache_timestamp'
    ];

    try {
      const staleKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(this.storagePrefix)) continue;
        // Different user -> stale.
        if (!key.startsWith(currentLsPrefix)) { staleKeys.push(key); continue; }
        // Current user -> only kill legacy vault-cache keys.
        const suffix = key.slice(currentLsPrefix.length);
        if (LEGACY_PREFIXES.some(p => suffix.startsWith(p))) staleKeys.push(key);
      }
      staleKeys.forEach(k => { try { localStorage.removeItem(k); } catch {} });
    } catch {}

    // Same legacy purge inside the current user's IDB.
    try {
      const db = await this.openDB();
      const tx = db.transaction('kv', 'readwrite');
      const store = tx.objectStore('kv');
      const req = store.getAllKeys();
      await new Promise((resolve) => {
        req.onsuccess = () => {
          const keys = req.result || [];
          for (const k of keys) {
            if (typeof k !== 'string') continue;
            if (!k.startsWith(currentLsPrefix)) { store.delete(k); continue; }
            const suffix = k.slice(currentLsPrefix.length);
            if (LEGACY_PREFIXES.some(p => suffix.startsWith(p))) store.delete(k);
          }
          resolve();
        };
        req.onerror = () => resolve();
      });
    } catch {}
  }

  /**
   * Check if storage is initialized and ready
   */
  isReady() {
    return this.initialized && this.cryptoKey !== null;
  }

  /**
   * Convert Uint8Array to base64 without stack overflow
   * @param {Uint8Array} bytes - Byte array to convert
   * @returns {string} - Base64 encoded string
   */
  uint8ArrayToBase64(bytes) {
    // Process in chunks to avoid stack overflow with large arrays
    const CHUNK_SIZE = 0x8000; // 32KB chunks
    let result = '';
    for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
      const chunk = bytes.subarray(i, Math.min(i + CHUNK_SIZE, bytes.length));
      result += String.fromCharCode.apply(null, chunk);
    }
    return btoa(result);
  }

  /**
   * Convert base64 to Uint8Array
   * @param {string} base64 - Base64 encoded string
   * @returns {Uint8Array} - Byte array
   */
  base64ToUint8Array(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Encrypt data using AES-GCM with the derived key
   * @param {any} data - Data to encrypt (will be JSON stringified)
   * @returns {string} - Base64 encoded encrypted data with IV
   */
  async encrypt(data) {
    if (!this.isReady()) {
      throw new Error('SecureStorage not initialized. Call initialize() first.');
    }

    // Use a replacer to handle circular references and non-serializable values
    const seen = new WeakSet();
    const jsonString = JSON.stringify(data, (key, value) => {
      // Skip functions and symbols
      if (typeof value === 'function' || typeof value === 'symbol') {
        return undefined;
      }
      // Handle circular references
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return undefined; // Remove circular reference
        }
        seen.add(value);
      }
      return value;
    });
    
    const encoder = new TextEncoder();
    const dataBytes = encoder.encode(jsonString);

    // Generate a random IV for each encryption (12 bytes for AES-GCM)
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Encrypt the data
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128 // Authentication tag length
      },
      this.cryptoKey,
      dataBytes
    );

    // Combine IV + encrypted data for storage
    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedData), iv.length);

    // Convert to base64 for localStorage (using chunked method to avoid stack overflow)
    return this.uint8ArrayToBase64(combined);
  }

  /**
   * Decrypt data using AES-GCM with the derived key
   * @param {string} encryptedBase64 - Base64 encoded encrypted data
   * @returns {any} - Decrypted and parsed data
   */
  async decrypt(encryptedBase64) {
    if (!this.isReady()) {
      throw new Error('SecureStorage not initialized. Call initialize() first.');
    }

    try {
      // Decode base64 using our chunked method
      const combined = this.base64ToUint8Array(encryptedBase64);

      // Extract IV and encrypted data
      const iv = combined.slice(0, 12);
      const encryptedData = combined.slice(12);

      // Decrypt the data
      const decryptedData = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv,
          tagLength: 128
        },
        this.cryptoKey,
        encryptedData
      );

      // Convert back to string and parse JSON
      const decoder = new TextDecoder();
      const jsonString = decoder.decode(decryptedData);
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data. This may be due to tampered data or wrong credentials.');
    }
  }

  /**
   * Open (or create) the IndexedDB database for this user
   * Returns a promise that resolves to the IDBDatabase
   */
  openDB() {
    return new Promise((resolve, reject) => {
      if (this._db) { resolve(this._db); return; }
      const dbName = `rfid_vault_${this.userId}`;
      const request = indexedDB.open(dbName, 1);
      request.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains('kv')) {
          db.createObjectStore('kv');
        }
      };
      request.onsuccess = (e) => {
        this._db = e.target.result;
        resolve(this._db);
      };
      request.onerror = (e) => {
        console.warn('[SecureStorage] IndexedDB open failed, will fallback to localStorage', e);
        reject(e);
      };
    });
  }

  /**
   * Save encrypted data — uses IndexedDB for large payloads, localStorage for small ones
   * @param {string} key - Storage key
   * @param {any} data - Data to store
   */
  async setItem(key, data) {
    return this._setItemSimple(key, data);
  }

  /**
   * Get and decrypt data — checks IndexedDB first, then localStorage
   * @param {string} key - Storage key
   * @returns {any} - Decrypted data or null if not found
   */
  async getItem(key) {
    return this._getItemSimple(key);
  }

  /**
   * Remove item from both IndexedDB and localStorage
   * @param {string} key - Storage key
   */
  removeItem(key) {
    const storageKey = this._makeStorageKey(key);
    this._removeEncrypted(storageKey);
  }

  /**
   * Clear all encrypted storage for current user
   */
  clearAll() {
    const prefix = `${this.storagePrefix}${this.userId}_`;
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Also clear IndexedDB
    this.openDB().then(db => {
      const tx = db.transaction('kv', 'readwrite');
      tx.objectStore('kv').clear();
    }).catch(() => {});
  }

  /**
   * Destroy the storage instance (logout)
   * Clears the encryption key from memory
   */
  destroy() {
    if (this._db) {
      try { this._db.close(); } catch(e) {}
      this._db = null;
    }
    this.cryptoKey = null;
    this.initialized = false;
    this.userId = null;
  }

  /**
   * Get all keys for current user
   * @returns {string[]} - Array of storage keys (without prefix)
   */
  getAllKeys() {
    const prefix = `${this.storagePrefix}${this.userId}_`;
    const keys = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(prefix)) {
        keys.push(key.replace(prefix, ''));
      }
    }
    
    return keys;
  }

  /**
   * Export encrypted vault data (for backup)
   * The exported data is STILL encrypted and cannot be read without proper auth
   * @returns {object} - Encrypted vault backup
   */
  async exportVault() {
    const keys = this.getAllKeys();
    const vault = {};
    
    for (const key of keys) {
      const storageKey = `${this.storagePrefix}${this.userId}_${key}`;
      vault[key] = localStorage.getItem(storageKey);
    }
    
    return {
      version: 1,
      origin: this.origin,
      userId: this.userId,
      timestamp: new Date().toISOString(),
      data: vault
    };
  }

  /**
   * Import encrypted vault data
   * @param {object} backup - Encrypted vault backup
   */
  async importVault(backup) {
    if (backup.origin !== this.origin) {
      throw new Error('Vault was encrypted on a different origin and cannot be imported');
    }
    
    if (backup.userId !== this.userId) {
      throw new Error('Vault belongs to a different user');
    }
    
    for (const [key, encryptedValue] of Object.entries(backup.data)) {
      const storageKey = `${this.storagePrefix}${this.userId}_${key}`;
      localStorage.setItem(storageKey, encryptedValue);
    }
  }
}

// Singleton instance
const secureStorage = new SecureStorage();
export default secureStorage;
